// Verdicts for the Diagnostics tab. Counts are scaled to a 24-hour rate so the
// same thresholds apply to every window.

export const VERDICT = {
  HEALTHY: 'healthy',
  WARNING: 'warning',
  UNHEALTHY: 'unhealthy',
  INSUFFICIENT: 'insufficient',
  NA: 'na',
};

const VERDICT_RANK = {
  [VERDICT.NA]: 0,
  [VERDICT.HEALTHY]: 1,
  [VERDICT.WARNING]: 2,
  [VERDICT.UNHEALTHY]: 3,
};

export const DEFAULT_DIAGNOSTICS_THRESHOLDS = {
  stabilityWarnPer24h: 2,
  stabilityUnhealthyPer24h: 4,
  rssiHealthyDbm: -67,
  rssiUnhealthyDbm: -75,
  asymmetryWarnDb: 10,
  publicMinSessions: 10,
  publicShortWarnPct: 30,
  publicShortUnhealthyPct: 50,
  minReportCoverage: 0.5,
};

export const DIAGNOSTICS_WINDOWS = [
  { value: '1h', label: '1h', hours: 1 },
  { value: '2h', label: '2h', hours: 2 },
  { value: '6h', label: '6h', hours: 6 },
  { value: '12h', label: '12h', hours: 12 },
  { value: '24h', label: '24h', hours: 24 },
];

const DEFAULT_REPORT_INTERVAL_SEC = 120;

export function windowHours(windowValue) {
  return DIAGNOSTICS_WINDOWS.find((w) => w.value === windowValue)?.hours ?? 24;
}

export function scaleTo24h(count, hours) {
  if (!Number.isFinite(count) || !hours) return 0;
  return (count * 24) / hours;
}

export function worstVerdict(verdicts) {
  return verdicts.reduce(
    (worst, v) => ((VERDICT_RANK[v] ?? 0) > (VERDICT_RANK[worst] ?? 0) ? v : worst),
    VERDICT.NA,
  );
}

export function stabilityScore(row) {
  const c = row?.event_counts || {};
  return (c.reconnects || 0) + (c.parent_changes || 0) + (c.reboots || 0);
}

export function stabilityVerdict(row, hours, t = DEFAULT_DIAGNOSTICS_THRESHOLDS) {
  if ((row?.event_counts?.report_gaps || 0) > 0) return VERDICT.UNHEALTHY;
  const rate = scaleTo24h(stabilityScore(row), hours);
  if (rate >= t.stabilityUnhealthyPer24h) return VERDICT.UNHEALTHY;
  if (rate >= t.stabilityWarnPer24h) return VERDICT.WARNING;
  return VERDICT.HEALTHY;
}

export function worstRssiP10(row) {
  const up = row?.uplink;
  if (!up) return null;
  const values = [up.sta_rssi?.p10, up.ap_rssi?.p10].filter((v) => Number.isFinite(v));
  return values.length ? Math.min(...values) : null;
}

export function rssiVerdict(row, t = DEFAULT_DIAGNOSTICS_THRESHOLDS) {
  const worst = worstRssiP10(row);
  if (worst === null) return VERDICT.NA;
  let verdict = VERDICT.HEALTHY;
  if (worst < t.rssiUnhealthyDbm) verdict = VERDICT.UNHEALTHY;
  else if (worst < t.rssiHealthyDbm) verdict = VERDICT.WARNING;
  const asym = row.uplink?.rssi_asymmetry_db;
  if (Number.isFinite(asym) && asym > t.asymmetryWarnDb) {
    verdict = worstVerdict([verdict, VERDICT.WARNING]);
  }
  return verdict;
}

export function publicWifiTotals(row) {
  let sessions = 0;
  let short = 0;
  (row?.public_interfaces || []).forEach((pi) => {
    sessions += pi.sessions || 0;
    short += pi.short_sessions || 0;
  });
  return { sessions, short, shortPct: sessions > 0 ? (short * 100) / sessions : null };
}

export function publicWifiVerdict(row, t = DEFAULT_DIAGNOSTICS_THRESHOLDS) {
  const graded = (row?.public_interfaces || []).filter((pi) => (pi.sessions || 0) >= t.publicMinSessions);
  if (graded.length === 0) return VERDICT.NA;
  return worstVerdict(graded.map((pi) => {
    const pct = pi.short_session_pct ?? ((pi.short_sessions || 0) * 100) / pi.sessions;
    if (pct > t.publicShortUnhealthyPct) return VERDICT.UNHEALTHY;
    if (pct >= t.publicShortWarnPct) return VERDICT.WARNING;
    return VERDICT.HEALTHY;
  }));
}

export function hasLowConfidence(row, hours, t = DEFAULT_DIAGNOSTICS_THRESHOLDS) {
  const samples = row?.reporting?.samples || 0;
  const interval = row?.reporting?.median_report_interval_sec || DEFAULT_REPORT_INTERVAL_SEC;
  const expected = (hours * 3600) / interval;
  if (expected <= 0) return false;
  return samples < expected * t.minReportCoverage;
}

export function evaluateDevice(row, hours, t = DEFAULT_DIAGNOSTICS_THRESHOLDS) {
  if (row?.no_reports) {
    const none = VERDICT.INSUFFICIENT;
    return { stability: none, rssi: none, backhaul: none, publicWifi: VERDICT.NA, overall: none, lowConfidence: true };
  }
  const stability = stabilityVerdict(row, hours, t);
  const rssi = rssiVerdict(row, t);
  const publicWifi = publicWifiVerdict(row, t);
  const lowConfidence = hasLowConfidence(row, hours, t);
  let backhaul = worstVerdict([stability, rssi]);
  if (lowConfidence && backhaul !== VERDICT.UNHEALTHY) backhaul = VERDICT.INSUFFICIENT;
  let overall = worstVerdict([stability, rssi, publicWifi]);
  if (lowConfidence && overall !== VERDICT.UNHEALTHY) overall = VERDICT.INSUFFICIENT;
  return { stability, rssi, backhaul, publicWifi, overall, lowConfidence };
}

const signalIntervals = (row) => (row?.event_counts?.rssi_below_threshold_intervals_sta || 0)
  + (row?.event_counts?.rssi_below_threshold_intervals_ap || 0);

const isDeviceGap = (ev) => ev.type === 'report_gap' && ev.cause !== 'nms_outage';

// Offline now comes from the devices API when the row carries its connected
// flag; rows without it fall back to an open report gap.
export function isOfflineNow(row) {
  if (typeof row?.connected === 'boolean') return !row.connected;
  return (row?.events || []).some((ev) => isDeviceGap(ev) && !ev.ended_at);
}

const offlineCount = (row) => (row?.event_counts?.report_gaps || 0) + (row?.connected === false ? 1 : 0);

export function isApOrStaRole(role) {
  const parts = String(role || '').toLowerCase().split('+').map((s) => s.trim()).filter(Boolean);
  return parts.includes('ap') || parts.includes('sta');
}

export const STABILITY_EVENTS = [
  { type: 'rssi_below_threshold', label: 'Signal', count: signalIntervals },
  { type: 'report_gap', label: 'Offline', count: offlineCount },
  { type: 'reconnect', label: 'Reconnects', count: (row) => row?.event_counts?.reconnects || 0 },
  { type: 'reboot', label: 'Reboots', count: (row) => row?.event_counts?.reboots || 0 },
  { type: 'parent_change', label: 'Parent changes', count: (row) => row?.event_counts?.parent_changes || 0 },
  { type: 'channel_change', label: 'Channel changes', count: (row, device) => apStaChannelCount(row, device) },
];

export const DEFAULT_STABILITY_EVENT = 'rssi_below_threshold';

export function hasStabilityEvent(row, type, device) {
  const def = STABILITY_EVENTS.find((e) => e.type === type);
  return def ? def.count(row, device) > 0 : true;
}

// Empty selection shows every device. Otherwise a device matches when it has
// any one of the selected event types.
export function matchesEventFilter(row, device, types) {
  if (!types?.length) return true;
  return types.some((type) => hasStabilityEvent(row, type, device));
}

// Devices with at least one event of each backhaul type, overall and per
// time bucket. Totals come from event_counts, which stay exact when a row's
// event list is truncated; buckets come from the events themselves. Channel
// change buckets keep AP and STA radios only. Signal intervals and report
// gaps mark every bucket they span; an open gap runs to the window end. NMS
// outage gaps are left out.
export function stabilityDeviceSeries(rows, start, end, buckets) {
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  const span = endMs - startMs;
  const out = {};
  STABILITY_EVENTS.forEach(({ type }) => {
    out[type] = { devices: 0, buckets: Array.from({ length: buckets }, () => new Set()) };
  });
  out.report_gap.current = 0;
  (rows || []).forEach((row) => {
    const serial = row?.device_mac_address;
    STABILITY_EVENTS.forEach(({ type, count }) => {
      if (count(row, row?.device) > 0) out[type].devices += 1;
    });
    if (isOfflineNow(row)) out.report_gap.current += 1;
    if (!(span > 0)) return;
    const bucketOf = (t) => Math.min(buckets - 1, Math.floor(((t - startMs) / span) * buckets));
    (row?.events || []).forEach((ev) => {
      if (ev.type === 'channel_change' && !isApStaChannelEvent(ev, row?.device)) return;
      const series = out[ev.type];
      if (!series || (ev.type === 'report_gap' && !isDeviceGap(ev))) return;
      const t = new Date(ev.event_at).getTime();
      let until = t;
      if (ev.ended_at) until = new Date(ev.ended_at).getTime();
      else if (ev.type === 'report_gap') until = endMs;
      if (!(until >= startMs && t <= endMs)) return;
      const last = bucketOf(Math.min(until, endMs));
      for (let i = bucketOf(Math.max(t, startMs)); i <= last; i += 1) series.buckets[i].add(serial);
    });
  });
  Object.values(out).forEach((s) => { s.buckets = s.buckets.map((set) => set.size); });
  return out;
}

const normKey = (v) => String(v || '').toLowerCase().replace(/:/g, '').trim();

export function isAiBoard(device) {
  const model = String(device?.capabilities?.model || '').toLowerCase();
  return model.includes('nvidia') || model.includes('jetson') || model.includes('qrb5165');
}

const compatibleOf = (device) => device?.capabilities?.compatible || device?.compatible || '';

// AI boards hosted by a JNET2, through the board's parent or a JNET2's
// provisioned AI device MAC. Diagnostics leaves these out.
export function jnet2AttachedAiSerials(devices) {
  const bySerial = new Map();
  const provisioned = new Set();
  (devices || []).forEach((d) => {
    const serial = normKey(d?.serial_number);
    if (!serial) return;
    bySerial.set(serial, d);
    if (compatibleOf(d) === 'jnet2') {
      [d.ai_mac_address, d.provisioning?.ai_device_mac].forEach((mac) => {
        const key = normKey(mac);
        if (key) provisioned.add(key);
      });
    }
  });
  const out = new Set();
  (devices || []).forEach((d) => {
    if (!isAiBoard(d)) return;
    const serial = normKey(d.serial_number);
    const parent = bySerial.get(normKey(d.parent_serial || d.last_known_parent_serial));
    if (provisioned.has(serial) || compatibleOf(parent) === 'jnet2') out.add(serial);
  });
  return out;
}

// Diagnostics rows joined with the devices API. Every row carries the
// device's connected flag and last_seen. Devices missing from diagnostics get
// a row: an offline one with an open gap from last_seen (or the window start)
// when the devices API says disconnected, otherwise a no-data row.
export function buildDiagnosticsRows(diagnostics, devices) {
  const excluded = jnet2AttachedAiSerials(devices);
  const bySerial = new Map();
  (devices || []).forEach((d) => {
    const serial = normKey(d?.serial_number);
    if (serial && !excluded.has(serial)) bySerial.set(serial, d);
  });
  const withDevice = (row, d) => (d
    ? { ...row, connected: typeof d.connected === 'boolean' ? d.connected : undefined, last_seen: d.last_seen || null }
    : row);

  const rows = [];
  const seen = new Set();
  (diagnostics?.devices || []).forEach((row) => {
    const serial = normKey(row.device_mac_address);
    if (excluded.has(serial)) return;
    seen.add(serial);
    rows.push(withDevice(row, bySerial.get(serial)));
  });

  const windowStart = diagnostics?.window_start;
  if (!windowStart) return rows;
  bySerial.forEach((d, serial) => {
    if (seen.has(serial)) return;
    const base = {
      device_mac_address: serial,
      no_reports: true,
      reporting: { samples: 0 },
      uplink: null,
      public_interfaces: [],
    };
    if (d.connected === false) {
      const lastSeen = d.last_seen && new Date(d.last_seen) > new Date(windowStart) ? d.last_seen : windowStart;
      rows.push(withDevice({
        ...base,
        event_counts: { report_gaps: 1 },
        events: [{ type: 'report_gap', event_at: lastSeen, cause: 'no_reports' }],
      }, d));
    } else {
      rows.push(withDevice({ ...base, event_counts: {}, events: [] }, d));
    }
  });
  return rows;
}

export function compactMac(value) {
  return String(value || '').toLowerCase().replace(/[^a-f0-9]/g, '');
}

const MAC_TERM = /^[0-9a-f]{1,4}([:.\-\s][0-9a-f]{1,4})+[:.\-\s]?$/;

export function matchesDeviceQuery(row, device, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const terms = q.split(',').map((t) => t.trim()).filter(Boolean);
  const serial = String(row?.device_mac_address || device?.serial_number || '').toLowerCase();
  const name = String(device?.display_name || device?.name || '').toLowerCase();
  const compactSerial = compactMac(serial);
  return terms.some((term) => {
    if (serial.includes(term) || name.includes(term)) return true;
    if (!MAC_TERM.test(term)) return false;
    const compact = compactMac(term);
    return compact.length >= 4 && compactSerial.includes(compact);
  });
}

export function isJttSsid(ssid) {
  return String(ssid || '').trim().toLowerCase().startsWith('jtt');
}

function ifaceRadioName(iface) {
  const radio = iface?.radio || iface?.Radio;
  if (typeof radio === 'string' && radio.trim()) return radio.trim();
  const named = String(radio?.name || radio?.Name || '').trim();
  if (named) return named;
  const ifaceName = String(iface?.name || iface?.Name || '');
  const match = ifaceName.match(/^(?:ath|wlan|wifi)(\d+)$/i);
  return match ? `wifi${match[1]}` : '';
}

export function jttOnlyRadioNames(device) {
  const ifaces = device?.private_wifi?.interfaces || [];
  const byRadio = new Map();
  ifaces.forEach((iface) => {
    const radio = ifaceRadioName(iface).toLowerCase();
    if (!radio) return;
    if (!byRadio.has(radio)) byRadio.set(radio, { jtt: false, other: false });
    const rec = byRadio.get(radio);
    const ssid = String(iface?.ssid || iface?.SSID || '').trim();
    const ifaceName = String(iface?.name || iface?.Name || '').toLowerCase();
    if (isJttSsid(ssid) || ifaceName.startsWith('jtt')) rec.jtt = true;
    else if (ssid) rec.other = true;
  });
  return new Set([...byRadio.entries()].filter(([, v]) => v.jtt && !v.other).map(([name]) => name));
}

export function isJttChannelEvent(ev, device) {
  if (ev?.type !== 'channel_change') return false;
  const radio = String(ev.radio_name || '').toLowerCase();
  return radio !== '' && jttOnlyRadioNames(device).has(radio);
}

// Channel changes the diagnostics strip counts: AP and STA radios. Public
// Wi-Fi and the technician JTT SSID are left out. A radio that carries AP or
// STA together with public still counts.
export function isApStaChannelEvent(ev, device) {
  if (ev?.type !== 'channel_change' || isJttChannelEvent(ev, device)) return false;
  return isApOrStaRole(ev.radio_role);
}

// The backend total counts AP and STA radios and is exact when the event
// list is truncated. Events the device list shows as JTT-only are removed.
function apStaChannelCount(row, device) {
  const dev = device || row?.device;
  const events = (row?.events || []).filter((ev) => ev.type === 'channel_change');
  const total = row?.event_counts?.channel_changes;
  if (typeof total !== 'number') return events.filter((ev) => isApStaChannelEvent(ev, dev)).length;
  const jtt = events.filter((ev) => isApOrStaRole(ev.radio_role) && isJttChannelEvent(ev, dev)).length;
  return Math.max(0, total - jtt);
}

// Events from one device report share event_at exactly. For each event, the
// size of its run of neighbours with the same event_at and its place in it.
export function sameReportGroups(events) {
  const list = events || [];
  const out = list.map(() => ({ size: 1, index: 0 }));
  let start = 0;
  for (let i = 1; i <= list.length; i += 1) {
    if (i < list.length && list[i].event_at && list[i].event_at === list[start].event_at) continue;
    for (let j = start; j < i; j += 1) out[j] = { size: i - start, index: j - start };
    start = i;
  }
  return out;
}

export function summarizeVerdicts(evaluated) {
  const empty = () => ({ healthy: 0, warning: 0, unhealthy: 0, insufficient: 0, na: 0 });
  const out = { stability: empty(), rssi: empty(), backhaul: empty(), publicWifi: empty(), overall: empty() };
  evaluated.forEach(({ verdicts }) => {
    Object.keys(out).forEach((topic) => {
      const v = verdicts[topic];
      if (out[topic][v] !== undefined) out[topic][v] += 1;
    });
  });
  return out;
}
